import Button from "@material-ui/core/Button/Button";
import FormControl from "@material-ui/core/FormControl/FormControl";
import FormGroup from "@material-ui/core/FormGroup/FormGroup";
import FormLabel from "@material-ui/core/FormLabel/FormLabel";
import Input from "@material-ui/core/Input/Input";
import InputAdornment from "@material-ui/core/InputAdornment/InputAdornment";
import InputLabel from "@material-ui/core/InputLabel/InputLabel";
import List from "@material-ui/core/List/List";
import ListItem from "@material-ui/core/ListItem/ListItem";
import Paper from "@material-ui/core/Paper/Paper";
import Typography from "@material-ui/core/Typography/Typography";
import AddCircle from "@material-ui/icons/AddCircle";
import Cancel from "@material-ui/icons/Cancel";
import CheckCircle from "@material-ui/icons/CheckCircle";
import Delete from "@material-ui/icons/Delete";
import Up from '@material-ui/icons/KeyboardArrowUp';
import SwapVert from "@material-ui/icons/SwapVert";
import * as React from "react";
import { DragDropContext, Draggable, DropResult, Droppable } from "react-beautiful-dnd";
import { DocumentItem } from "../model/autogen/DocumentItem";
import { WithDefaultStyles, withDefaultStyles } from "../utils/createStyles";
import { NBSP } from "../utils/string";
import { RTEField } from "./forms/rte-field";
import { isArrayNullOrEmpty } from "../utils/array";

// a little function to help us with reordering the result
const reorder = (list: any, startIndex: number, endIndex: number) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
};

const grid = 8;

const getItemStyle = (isDragging: boolean, draggableStyle: any) => ({
    // some basic styles to make the items look a bit nicer
    userSelect: 'none',
    padding: grid * 2,
    margin: `0 0 ${grid}px 0`,

    // change background colour if dragging
    background: isDragging ? 'lightgreen' : 'grey',

    // styles we need to apply on draggables
    ...draggableStyle,
});

const getListStyle = (isDraggingOver: boolean) => ({
    background: isDraggingOver ? 'lightblue' : 'lightgrey',
    padding: grid,
    width: 250,
});

interface IRTEListItemProps {
    index: number;
    title: string;
    name: string;
    value: string;
    onChange: (index: number, title: string, value: string) => void;
    onRequestRemoval: (index: number) => void;
    bringToTop?(index:number): void;
}

class RTEListItemNoStyle extends React.Component<IRTEListItemProps & WithDefaultStyles, any> {
    constructor(props: IRTEListItemProps & WithDefaultStyles) {
        super(props);
    }
    private onContentItemChanged = (e: any) => {
        this.props.onChange(this.props.index, this.props.title, e.target.value);
    }
    private onContentTitleChanged = (e: any) => {
        this.props.onChange(this.props.index, e.target.value, this.props.value);
    }
    private onRemove = (e: any) => {
        if (confirm(`Are you sure you want to remove this item?`)) {
            this.props.onRequestRemoval(this.props.index);
        }
    }
    private bringToTop = () =>{
        if(this.props.bringToTop)
        {
            this.props.bringToTop(this.props.index);
        }
    }

    render(): JSX.Element {
        const { name, title, value, index, classes } = this.props;
        const adorn = <InputAdornment position="end">
        <Button color="secondary" onClick={this.onRemove}><Delete />{NBSP}Remove</Button>
        <Button color="primary" onClick={()=> this.bringToTop()}><Up /> Push To Top</Button>
        </InputAdornment>;
        return <ListItem>
            <FormControl fullWidth={true}>
                <FormControl className={classes.input}>
                    <InputLabel>Title</InputLabel>
                    <Input fullWidth onChange={this.onContentTitleChanged} name="title" value={title} endAdornment={adorn} />
                </FormControl>
                <FormGroup className={classes.input}>
                    <FormLabel>Content</FormLabel>
                    <RTEField name={name} key={index} value={value} onChange={this.onContentItemChanged} />
                </FormGroup>
            </FormControl>
        </ListItem>;
    }
}

const RTEListItem = withDefaultStyles(RTEListItemNoStyle);


export interface IEditableDocumentItemListProps {
    items: DocumentItem[];
    onChange: (index: number, title: string, value: string) => void;
    onAdd: () => void;
    onRemove: (index: number) => void;
    /**
     * Raised when reordering has completed. The argument is the list item ids in their new intended order
     */
    onReordered: (items: { id: string }[]) => void;
    noItemsPlaceholder?: JSX.Element;
}

export interface INoEditableDocumentsProps {
    itemKindPlural: string;
    newItemKindSingular: string;
}

export const NoEditableDocuments = (props: INoEditableDocumentsProps) => {
    return <Paper style={{ padding: 16, marginTop: 16 }}>
        <Typography>You currently have no {props.itemKindPlural}. Hit the <strong>Add Item</strong> button above to add {props.newItemKindSingular}</Typography>
    </Paper>;
}

export interface IEditableDocumentItemListState {
    itemsToReorder?: any[];
}

export class EditableDocumentItemList extends React.Component<IEditableDocumentItemListProps, IEditableDocumentItemListState> {
    constructor(props: IEditableDocumentItemListProps) {
        super(props);
        this.state = {

        };
    }
    private onStartReordering = (e: any) => {
        this.setState({ itemsToReorder: this.props.items.map(it => ({ id: it.guid, content: it.title })) });
    }
    private onFinishReordering = (e: any) => {
        if (this.state.itemsToReorder) {
            this.props.onReordered(this.state.itemsToReorder);
            this.setState({ itemsToReorder: undefined });
        }
    }
    private onCancelReordering = (e: any) => {
        this.setState({ itemsToReorder: undefined });
    }
    private onDragEnd = (result: DropResult) => {
        // dropped outside the list
        if (!result.destination) {
            return;
        }

        const items = reorder(
            this.state.itemsToReorder,
            result.source.index,
            result.destination.index
        );

        this.setState({
            itemsToReorder: items
        });
    }

    private bringToTop = (index:number) => {
        var itemsToReorder = this.props.items.map(it => ({ id: it.guid, content: it.title }))
        const items:any[] = reorder(
            itemsToReorder,
            index,
            0
        );

        if (items) {
            this.props.onReordered(items);
            this.setState({ itemsToReorder: undefined });
        }
    }

    render(): JSX.Element {
        const { items, noItemsPlaceholder } = this.props;
        const { itemsToReorder } = this.state;
        return <List>
            {(() => {
                if (itemsToReorder) { //Re-ordering
                    return <>
                        <Button color="primary" variant="contained" onClick={this.onFinishReordering}><CheckCircle />{NBSP} Finish re-ordering</Button>{NBSP}
                        <Button color="secondary" variant="contained" onClick={this.onCancelReordering}><Cancel />{NBSP} Cancel re-ordering</Button>
                    </>;
                    
                } else { //Not re-ordering
                    return <>
                        <Button color="primary" variant="contained" onClick={this.props.onAdd}><AddCircle />{NBSP}Add Item</Button>{NBSP}
                        <Button color="secondary" variant="contained" onClick={this.onStartReordering}><SwapVert />{NBSP}Re-Order Items</Button>
                    </>;
                }
            })()}
            {(() => {
                if (isArrayNullOrEmpty(items)) {
                    return noItemsPlaceholder;
                } else {
                    if (itemsToReorder) {
                        return <DragDropContext onDragEnd={this.onDragEnd}>
                            <Droppable droppableId="droppable">
                                {(provided, snapshot) => (
                                    <div ref={provided.innerRef}
                                        style={getListStyle(snapshot.isDraggingOver)}>
                                        {itemsToReorder.map((item, index) => (
                                            <Draggable key={item.id} draggableId={item.id} index={index}>
                                                {(provided, snapshot) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        style={getItemStyle(
                                                            snapshot.isDragging,
                                                            provided.draggableProps.style
                                                        )}
                                                    >
                                                        {item.content}
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    } else {
                        return items.map((item, index) => <RTEListItem bringToTop={this.bringToTop} key={index} index={index} name={`document-item-${index}`} title={item.title} value={item.content} onChange={this.props.onChange} onRequestRemoval={this.props.onRemove} />);
                    }
                }
            })()}
        </List>;
    }
}


// WEBPACK FOOTER //
// ./src/components/editable-document-item-list.tsx